import { useEffect, useState, type FormEvent, type JSX } from 'react';
import { useSearch } from '@tanstack/react-router';
import JSZip from 'jszip';

import { shaktiApi, ApiError } from '../api/client';
import { useProjectScopedEffect } from '../stores/project';
import type { Artifact } from '../api/types';
import { useProjectStore } from '../stores/project';
import { DiffViewer } from '../components/DiffViewer';

/**
 * Artifact Workspace (FEAT-U04 / FEAT-ADD-014) + W1.1 (v1.1).
 *
 * 3-pane layout: tree (list) / viewer (readonly content) / metadata.
 * W1.1 swaps the legacy <pre> viewer for the Monaco-backed DiffViewer
 * (single-pane, empty baseline) so code is syntax-highlighted using
 * the artifact's file extension. The viewer respects reduced-motion
 * and falls back to the CSS renderer when Monaco is unavailable.
 */
export function ArtifactsRoute(): JSX.Element {
  const search = useSearch({ strict: false }) as { packet_id?: string };
  const packetId = search.packet_id;

  const [artifacts, setArtifacts] = useState<Artifact[]>([]);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [content, setContent] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [verifying, setVerifying] = useState(false);
  const [verifyResult, setVerifyResult] = useState<string | null>(null);

  // N5.5 / W1.1 (v1.1) — project-scoped; aborts in-flight on switch so
  // a prior project's artifact list can never leak into the new view.
  useProjectScopedEffect(
    (signal) => {
      if (!packetId) {
        setArtifacts([]);
        return;
      }
      shaktiApi
        .listArtifacts(packetId, { signal })
        .then((r) => {
          if (!signal.aborted) setArtifacts(r);
        })
        .catch((e: unknown) => {
          if (signal.aborted) return;
          setError(
            e instanceof ApiError ? e.body?.error ?? e.message : 'Failed to list artifacts',
          );
        });
    },
    [packetId],
  );

  useEffect(() => {
    if (!selectedId) return;
    const ac = new AbortController();
    setContent(null);
    shaktiApi
      .getArtifactContent(selectedId, { signal: ac.signal })
      .then(setContent)
      .catch((e: unknown) => {
        if (ac.signal.aborted) return;
        setContent(`// failed to load: ${e instanceof Error ? e.message : 'unknown error'}`);
      });
    return () => ac.abort();
  }, [selectedId]);

  const selected = selectedId ? artifacts.find((a) => a.artifact_id === selectedId) : null;

  const onVerify = async (): Promise<void> => {
    if (!selectedId) return;
    setVerifying(true);
    try {
      const r = await shaktiApi.verifyArtifact(selectedId);
      setVerifyResult(r.verified ? `verified sha=${r.sha256.slice(0, 12)}…` : 'FAILED');
    } catch (e) {
      setVerifyResult(`error: ${e instanceof Error ? e.message : 'unknown'}`);
    } finally {
      setVerifying(false);
    }
  };

  // K1.3 (round-5) — client-side zip + download.
  const [zipping, setZipping] = useState(false);
  const [showPush, setShowPush] = useState(false);
  const [toast, setToast] = useState<string | null>(null);
  const projectId = useProjectStore((s) => s.activeProjectId);
  const projectSlug = useProjectStore(
    (s) => s.projects.find((p) => p.id === s.activeProjectId)?.slug ?? 'shakti-app',
  );

  const onDownloadZip = async (): Promise<void> => {
    if (artifacts.length === 0 || !packetId) return;
    setZipping(true);
    try {
      const zip = new JSZip();
      // Fetch each artifact's content and add to zip under its tree path.
      await Promise.all(
        artifacts.map(async (a) => {
          try {
            const content = await shaktiApi.getArtifactContent(a.artifact_id);
            const body = typeof content === 'string' ? content : JSON.stringify(content, null, 2);
            zip.file(a.path || `artifact-${a.artifact_id}.txt`, body);
          } catch {
            zip.file(
              `${a.path || a.artifact_id}.error.txt`,
              `Failed to fetch artifact ${a.artifact_id}`,
            );
          }
        }),
      );
      const blob = await zip.generateAsync({ type: 'blob' });
      const url = URL.createObjectURL(blob);
      const anchor = document.createElement('a');
      anchor.href = url;
      anchor.download = `${projectSlug}-${packetId.slice(0, 8)}.zip`;
      document.body.appendChild(anchor);
      anchor.click();
      anchor.remove();
      URL.revokeObjectURL(url);
      setToast('Download started.');
      setTimeout(() => setToast(null), 3000);
    } catch (e) {
      setToast(`Download failed: ${e instanceof Error ? e.message : 'unknown'}`);
      setTimeout(() => setToast(null), 4000);
    } finally {
      setZipping(false);
    }
  };

  return (
    <section aria-labelledby="art-title" className="mx-auto flex max-w-7xl flex-col gap-4">
      <header>
        <span className="shakti-mono text-2xs uppercase tracking-wider text-fg-muted">
          /dashboard/artifacts
        </span>
        <h1 id="art-title" className="text-2xl font-semibold text-fg-primary">
          Artifact Workspace
        </h1>
        {packetId ? (
          <p className="text-sm text-fg-secondary">
            Packet <code className="shakti-mono">{packetId}</code> — {artifacts.length} artifacts
          </p>
        ) : (
          <p className="text-sm text-fg-muted">
            Pass <code>?packet_id=…</code> to load artifacts for a packet.
          </p>
        )}
      </header>

      {error ? (
        <div role="alert" className="shakti-card border border-status-error/40 text-status-error">
          {error}
        </div>
      ) : null}

      {toast ? (
        <div
          role="status"
          aria-live="polite"
          className="shakti-card border border-status-success/40 bg-status-success/10 text-sm text-status-success"
        >
          {toast}
        </div>
      ) : null}

      {packetId && artifacts.length > 0 ? (
        <div className="flex gap-2" data-testid="artifact-actions">
          <button
            type="button"
            onClick={onDownloadZip}
            disabled={zipping}
            className="rounded-sm border border-border-subtle bg-bg-surface px-3 py-1.5 text-xs disabled:opacity-50"
            data-testid="download-zip"
          >
            {zipping ? 'Packaging…' : 'Download .zip'}
          </button>
          <button
            type="button"
            onClick={() => setShowPush(true)}
            className="rounded-sm bg-fg-primary px-3 py-1.5 text-xs font-semibold text-bg-base"
            data-testid="push-to-vcs"
          >
            Push to GitHub / Open PR
          </button>
        </div>
      ) : null}

      <div className="grid grid-cols-[220px_1fr_280px] gap-4">
        <aside className="shakti-card overflow-y-auto" aria-label="Artifact tree">
          <h2 className="mb-2 text-2xs font-semibold uppercase tracking-wider text-fg-muted">
            Tree
          </h2>
          {artifacts.length === 0 ? (
            <p className="text-xs text-fg-muted">
              {packetId ? 'No artifacts yet.' : 'Pass ?packet_id=…'}
            </p>
          ) : (
            <ul className="flex flex-col gap-0.5">
              {artifacts.map((a) => (
                <li key={a.artifact_id}>
                  <button
                    type="button"
                    onClick={() => setSelectedId(a.artifact_id)}
                    className={`block w-full truncate rounded-sm px-2 py-1 text-left text-xs font-mono transition-colors ${
                      selectedId === a.artifact_id
                        ? 'bg-accent-muted text-fg-primary'
                        : 'text-fg-secondary hover:bg-bg-raised'
                    }`}
                    title={a.path}
                  >
                    {a.path}
                  </button>
                </li>
              ))}
            </ul>
          )}
        </aside>

        <div className="shakti-card flex flex-col gap-2 overflow-hidden" aria-label="Artifact viewer">
          <h2 className="text-2xs font-semibold uppercase tracking-wider text-fg-muted">
            Viewer
          </h2>
          {content !== null ? (
            <div className="max-h-[60vh] overflow-auto">
              <DiffViewer
                oldText=""
                newText={content}
                oldTitle="Baseline"
                newTitle={selected?.path ?? 'Artifact'}
                splitView={false}
                monaco
                filename={selected?.path}
              />
            </div>
          ) : (
            <p className="text-xs text-fg-muted">Select an artifact to view its content.</p>
          )}
        </div>

        <aside className="shakti-card" aria-label="Artifact metadata">
          <h2 className="mb-2 text-2xs font-semibold uppercase tracking-wider text-fg-muted">
            Metadata
          </h2>
          {selected ? (
            <dl className="flex flex-col gap-2 text-xs">
              <MetaRow k="ID" v={selected.artifact_id} mono />
              <MetaRow k="Kind" v={selected.kind} />
              <MetaRow k="Path" v={selected.path} mono />
              <MetaRow k="SHA-256" v={selected.sha256.slice(0, 24) + '…'} mono />
              <MetaRow k="Size" v={`${selected.size_bytes.toLocaleString()} bytes`} />
              <MetaRow k="Created" v={new Date(selected.created_at).toLocaleString()} />
              <button
                type="button"
                onClick={onVerify}
                disabled={verifying}
                className="mt-2 rounded-sm border border-border-subtle px-2 py-1 text-2xs text-fg-secondary hover:text-fg-primary disabled:opacity-40"
              >
                {verifying ? 'Verifying…' : 'Verify signature'}
              </button>
              {verifyResult ? (
                <p className="text-2xs text-fg-muted">{verifyResult}</p>
              ) : null}
            </dl>
          ) : (
            <p className="text-xs text-fg-muted">Select an artifact.</p>
          )}
        </aside>
      </div>

      {showPush && packetId && projectId ? (
        <PushModal
          packetId={packetId}
          projectId={projectId}
          defaultBranch={`shakti/${projectSlug}-${Date.now().toString(36)}`}
          onClose={() => setShowPush(false)}
          onSuccess={(msg) => {
            setShowPush(false);
            setToast(msg);
            setTimeout(() => setToast(null), 6000);
          }}
        />
      ) : null}
    </section>
  );
}

function MetaRow({ k, v, mono = false }: { k: string; v: string; mono?: boolean }): JSX.Element {
  return (
    <div>
      <dt className="text-fg-muted">{k}</dt>
      <dd className={mono ? 'shakti-mono text-fg-primary' : 'text-fg-primary'}>{v}</dd>
    </div>
  );
}

// ── K1.3 (round-5) — Push to GitHub / Open PR modal ─────────────────────────

interface PushModalProps {
  packetId: string;
  projectId: string;
  defaultBranch: string;
  onClose: () => void;
  onSuccess: (msg: string) => void;
}

export function PushModal({
  packetId,
  projectId,
  defaultBranch,
  onClose,
  onSuccess,
}: PushModalProps): JSX.Element {
  const [provider, setProvider] = useState<'github' | 'gitlab'>('github');
  const [repoOwner, setRepoOwner] = useState('');
  const [repoName, setRepoName] = useState('');
  const [branch, setBranch] = useState(defaultBranch);
  const [headSha, setHeadSha] = useState('HEAD');
  const [autoMerge, setAutoMerge] = useState(false);
  const [prTitle, setPrTitle] = useState('Shakti-generated changes');
  const [prBody, setPrBody] = useState('Automated changes from the Shakti SDLC pipeline.');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function submit(e: FormEvent<HTMLFormElement>): Promise<void> {
    e.preventDefault();
    setError(null);
    setBusy(true);
    try {
      await shaktiApi.vcsPush(packetId, {
        project_id: projectId,
        provider,
        repo_owner: repoOwner.trim(),
        repo_name: repoName.trim(),
        branch: branch.trim(),
        head_sha: headSha.trim(),
        force: false,
      });
      const pr = await shaktiApi.vcsOpenPr(packetId, {
        project_id: projectId,
        provider,
        repo_owner: repoOwner.trim(),
        repo_name: repoName.trim(),
        source_branch: branch.trim(),
        target_branch: 'main',
        title: prTitle,
        body: prBody,
        auto_merge: autoMerge,
      });
      // K3.4 (round-5) — surface the auto-merge outcome verbatim.
      // When the remote declines (CI red, protection, conflict) the
      // server returns merged:false with the human reason; don't
      // pretend it merged.
      let summary = `Opened PR #${pr.pr.number}: ${pr.pr.html_url}`;
      if (pr.merge) {
        summary += pr.merge.merged
          ? ` — merged via ${pr.merge.method}${pr.merge.sha ? ` (${pr.merge.sha.slice(0, 8)})` : ''}`
          : ` — auto-merge declined: ${pr.merge.message ?? 'no details'}`;
      }
      onSuccess(summary);
    } catch (e) {
      setError(
        e instanceof ApiError
          ? `${e.body?.error ?? e.message} (${e.status})`
          : e instanceof Error
            ? e.message
            : 'Push failed',
      );
      setBusy(false);
    }
  }

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="push-title"
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4"
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <form
        onSubmit={submit}
        className="shakti-card w-full max-w-lg bg-bg-base p-6"
        data-testid="push-modal"
      >
        <header className="mb-4 flex items-start justify-between">
          <h3 id="push-title" className="text-lg font-semibold">
            Push + open PR
          </h3>
          <button type="button" onClick={onClose} aria-label="Close" className="text-fg-muted">
            ×
          </button>
        </header>
        <p className="mb-3 text-xs text-fg-muted">
          Requires VCS credentials configured for this project in Settings → VCS credentials.
        </p>

        <div className="flex flex-col gap-3">
          <label className="flex flex-col gap-1 text-xs">
            Provider
            <select
              value={provider}
              onChange={(e) => setProvider(e.target.value as 'github' | 'gitlab')}
              className="rounded-sm border border-border-subtle bg-bg-surface px-2 py-1 text-sm"
              disabled={busy}
            >
              <option value="github">GitHub</option>
              <option value="gitlab">GitLab</option>
            </select>
          </label>
          <div className="grid grid-cols-2 gap-3">
            <label className="flex flex-col gap-1 text-xs">
              Repo owner
              <input
                value={repoOwner}
                onChange={(e) => setRepoOwner(e.target.value)}
                required
                disabled={busy}
                placeholder="acme"
                className="rounded-sm border border-border-subtle bg-bg-surface px-2 py-1 text-sm"
              />
            </label>
            <label className="flex flex-col gap-1 text-xs">
              Repo name
              <input
                value={repoName}
                onChange={(e) => setRepoName(e.target.value)}
                required
                disabled={busy}
                placeholder="widget"
                className="rounded-sm border border-border-subtle bg-bg-surface px-2 py-1 text-sm"
              />
            </label>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <label className="flex flex-col gap-1 text-xs">
              Branch
              <input
                value={branch}
                onChange={(e) => setBranch(e.target.value)}
                required
                disabled={busy}
                className="shakti-mono rounded-sm border border-border-subtle bg-bg-surface px-2 py-1 text-sm"
              />
            </label>
            <label className="flex flex-col gap-1 text-xs">
              Head SHA
              <input
                value={headSha}
                onChange={(e) => setHeadSha(e.target.value)}
                required
                disabled={busy}
                className="shakti-mono rounded-sm border border-border-subtle bg-bg-surface px-2 py-1 text-sm"
              />
            </label>
          </div>
          <label className="flex flex-col gap-1 text-xs">
            PR title
            <input
              value={prTitle}
              onChange={(e) => setPrTitle(e.target.value)}
              required
              disabled={busy}
              className="rounded-sm border border-border-subtle bg-bg-surface px-2 py-1 text-sm"
            />
          </label>
          <label className="flex flex-col gap-1 text-xs">
            PR body
            <textarea
              value={prBody}
              onChange={(e) => setPrBody(e.target.value)}
              rows={3}
              disabled={busy}
              className="rounded-sm border border-border-subtle bg-bg-surface px-2 py-1 text-sm"
            />
          </label>
          <label className="flex items-center gap-2 text-xs">
            <input
              type="checkbox"
              checked={autoMerge}
              onChange={(e) => setAutoMerge(e.target.checked)}
              disabled={busy}
            />
            Auto-merge to default branch after CI green (K3.4, opt-in, never force-push)
          </label>
          {error ? (
            <div
              role="alert"
              className="rounded-sm border border-red-500/40 bg-red-500/10 px-3 py-2 text-xs text-red-200"
            >
              {error}
            </div>
          ) : null}
          <div className="mt-2 flex justify-end gap-2">
            <button
              type="button"
              onClick={onClose}
              disabled={busy}
              className="rounded-sm border border-border-subtle bg-bg-surface px-3 py-1.5 text-xs"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={busy || !repoOwner.trim() || !repoName.trim()}
              className="rounded-sm bg-fg-primary px-3 py-1.5 text-xs font-semibold text-bg-base disabled:opacity-50"
              data-testid="push-submit"
            >
              {busy ? 'Pushing…' : 'Push + open PR'}
            </button>
          </div>
        </div>
      </form>
    </div>
  );
}
